import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import SignInPrompt from '../../components/ui/SignInPrompt';
import ChannelAmpDropdown from '../amps/ChannelAmpDropdown';
import {FiSend,FiPaperclip,FiMic,FiUsers,FiFileText,FiZap,FiX,FiClock,FiFolder,} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { QUICK_ACTIONS } from '../../constants/mockData';
import type { FileAttachment } from './chat.types';
import * as fileUploadService from '../../services/fileUploadService';
import SelectFromAmsDialog, { AmsUploadSelection } from '../momentumcommand/SelectFromAmsDialog';
import { useInsuredMention, buildEntityXmlTag } from './useInsuredMention';
import InsuredMentionDropdown from './InsuredMentionDropdown';
import { getSuggestedQuestions } from '../../services/ampService';
import '../../styles/ChatInput.css';
import fileIcon from '../../assets/images/FileIcon.png';
 
interface AttachedFile {
  id: string;
  file: File;
  preview?: string;
  uploading?: boolean;
  uploadError?: string;
  fileUrl?: string;
  /** True when the file was picked from the AMS drive (already uploaded server-side). */
  fromAms?: boolean;
  /** Real byte size of an AMS file — the stub File object has no content, so its
   *  own .size is always 0. */
  amsSize?: number;
}
 
interface ChatInputProps {
  onSend: (message: string, attachments?: FileAttachment[], apiPrompt?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  externalValue?: string;
  selectedAmpId?: string | null;
  onAmpSelect?: (ampId: string) => void;
  conversationId?: string; // Required for file upload API
  hideAmpDropdown?: boolean;
  hideAttachButton?: boolean;
  /** Overrides the AMP-dropdown trigger label (e.g. a Digital Workforce agent's name). */
  overrideAmpName?: string;
  /** The active Digital Workforce agent's id — enables the agent-switcher menu and highlights the current agent. */
  overrideAgentId?: string;
}
 
const ACTION_ICONS: Record<string, React.ReactNode> = {
  leads: <FiUsers size={13} />,
  quotes: <FiFileText size={13} />,
  workflow: <FiZap size={13} />,
};
 
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
 
const ChatInput: React.FC<ChatInputProps> = ({ onSend, disabled, placeholder, externalValue, selectedAmpId, onAmpSelect, conversationId, hideAmpDropdown, hideAttachButton, overrideAmpName, overrideAgentId }) => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const [value, setValue] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [signInPrompt, setSignInPrompt] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [showAmsSelector, setShowAmsSelector] = useState(false);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  // Silently persist uploaded file refs across messages without showing in UI
  const persistedFileRefsRef = useRef<FileAttachment[]>([]);
  const dragCounterRef = useRef(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const micBaseValueRef = useRef('');
  const micAccumulatedRef = useRef(''); // final transcripts accumulated during session

  // Helper to generate storage key for attached files
  const getAttachedFilesStorageKey = useCallback(() => {
    return conversationId ? `chat-attached-files-${conversationId}` : null;
  }, [conversationId]);

  // Persist attached files to sessionStorage
  useEffect(() => {
    const storageKey = getAttachedFilesStorageKey();
    if (!storageKey) return;

    try {
      // Only store files that have completed uploading
      const uploadedFiles = attachedFiles.filter(f => f.fileUrl && !f.uploading && !f.uploadError);
      if (uploadedFiles.length > 0) {
        const filesData = uploadedFiles.map(f => ({
          name: f.file.name,
          size: f.file.size,
          type: f.file.type,
          fileUrl: f.fileUrl,
          fromAms: f.fromAms,
          amsSize: f.amsSize,
        }));
        sessionStorage.setItem(storageKey, JSON.stringify(filesData));
      }
    } catch (err) {
      console.warn('Failed to persist attached files:', err);
    }
  }, [attachedFiles, getAttachedFilesStorageKey]);

  // Restore attached files from sessionStorage on mount
  useEffect(() => {
    const storageKey = getAttachedFilesStorageKey();
    if (!storageKey) return;

    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const filesData = JSON.parse(stored);
        const restoredFiles: AttachedFile[] = filesData.map((f: any) => ({
          id: `restored-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: new File([], f.name, { type: f.type }),
          fileUrl: f.fileUrl,
          uploading: false,
          fromAms: f.fromAms,
          amsSize: f.amsSize,
        }));
        setAttachedFiles(restoredFiles);
        // Also restore to persistedFileRefsRef for subsequent messages
        persistedFileRefsRef.current = filesData;
      }
    } catch (err) {
      console.warn('Failed to restore attached files:', err);
    }
  }, [conversationId, getAttachedFilesStorageKey]);

  // Auto-resize textarea up to MAX_LINES, then scroll
  const adjustHeight = useCallback(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const style = window.getComputedStyle(ta);
    const lineH = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5 || 22;
    const padV = parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
    const max = lineH * 5 + padV;
    ta.style.height = Math.min(ta.scrollHeight, max) + 'px';
    ta.style.overflowY = ta.scrollHeight > max ? 'auto' : 'hidden';
  }, []);
 
  // AMP suggested questions
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [suggestionsActiveIdx, setSuggestionsActiveIdx] = useState(-1);
  const suggestionsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
 
  const {
    mentionActive,
    mentionQuery,
    mentionResults,
    mentionLoading,
    mentionActiveIdx,
    setMentionActiveIdx,
    insertedNames,
    insertedEntities,
    clearInsertedNames,
    onValueChange: mentionOnValueChange,
    onKeyDown: mentionOnKeyDown,
    selectMention,
    closeMention,
  } = useInsuredMention();
 
  // Adjust height on every value change
  useEffect(() => { adjustHeight(); }, [value, adjustHeight]);

  // Re-focus input when response finishes (disabled flips false → user can type immediately)
  const prevDisabledRef = useRef(disabled);
  useEffect(() => {
    if (prevDisabledRef.current && !disabled) {
      inputRef.current?.focus();
    }
    prevDisabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    if (externalValue) {
      setValue(externalValue);
      setTimeout(() => { inputRef.current?.focus(); adjustHeight(); }, 0);
    }
  }, [externalValue, adjustHeight]);
 
  useEffect(() => {
    setValue('');
    setAttachedFiles([]);
    setSuggestedQuestions([]);
    setSuggestionsActiveIdx(-1);
    closeMention();
    clearInsertedNames();
  }, [selectedAmpId]);

  useEffect(() => {
    setAttachedFiles([]);
    persistedFileRefsRef.current = [];
  }, [conversationId]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newAttachments: AttachedFile[] = files.map((file) => ({
      id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      uploading: false,
    }));
    setAttachedFiles((prev) => [...prev, ...newAttachments]);
    e.target.value = '';
   
    if (conversationId) {
      await uploadFilesImmediately(newAttachments);
    }
  }, [conversationId]);
 
  const uploadFilesImmediately = async (filesToUpload: AttachedFile[]) => {
    if (!conversationId) {
      console.error('Cannot upload files without conversation ID');
      return;
    }
 
    for (const attachedFile of filesToUpload) {
      setAttachedFiles((prev) =>
        prev.map((f) => (f.id === attachedFile.id ? { ...f, uploading: true, uploadError: undefined } : f))
      );
 
      try {
        const uploadResponse = await fileUploadService.uploadFile(attachedFile.file, conversationId);
 
        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.id === attachedFile.id
              ? { ...f, uploading: false, fileUrl: uploadResponse.fileUrl }
              : f
          )
        );
        // Persist file ref silently for subsequent messages
        persistedFileRefsRef.current.push({
          name: attachedFile.file.name,
          size: attachedFile.file.size,
          type: attachedFile.file.type,
          fileUrl: uploadResponse.fileUrl,
        });
      } catch (error) {
        console.error('File upload failed:', error);
        setAttachedFiles((prev) =>
          prev.map((f) =>
            f.id === attachedFile.id
              ? { ...f, uploading: false, uploadError: 'Upload failed' }
              : f
          )
        );
      }
    }
  };
 
  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  // ── Clipboard paste support: paste images directly into the chat input ──
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (imageItems.length === 0) return; // No images — let normal text paste happen

    e.preventDefault(); // Prevent pasting image as text/base64

    const newAttachments: AttachedFile[] = imageItems.map(item => {
      const file = item.getAsFile();
      if (!file) return null;
      // Generate a meaningful filename: screenshot-2026-07-28-123456.png
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const ext = file.type.split('/')[1] || 'png';
      const namedFile = new File([file], `screenshot-${ts}.${ext}`, { type: file.type });
      return {
        id: `paste-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: namedFile,
        preview: URL.createObjectURL(file),
        uploading: false,
      };
    }).filter(Boolean) as AttachedFile[];

    if (newAttachments.length === 0) return;

    setAttachedFiles(prev => [...prev, ...newAttachments]);

    if (conversationId) {
      await uploadFilesImmediately(newAttachments);
    }
  }, [conversationId]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const newAttachments: AttachedFile[] = files.map((file) => ({
      id: `file-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      uploading: false,
    }));
    setAttachedFiles((prev) => [...prev, ...newAttachments]);

    if (conversationId) {
      await uploadFilesImmediately(newAttachments);
    }
  }, [conversationId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = value.trim();
      // Require text message - files alone cannot be sent
      if (!trimmed || disabled || isUploading) return;
 
 
      const stillUploading = attachedFiles.some(f => f.uploading);
      if (stillUploading) {
        return;
      }
 
      const failedFiles = attachedFiles.filter(f => f.uploadError);
      if (failedFiles.length > 0) {
        alert('Some files failed to upload. Please remove them and try again.');
        return;
      }
 
      // Upload files if conversationId wasn't available during selection
      let finalAttachedFiles = attachedFiles;
      const filesToUpload = attachedFiles.filter(f => !f.fileUrl);
      if (filesToUpload.length > 0) {
        if (!conversationId) {
          alert('Cannot upload files without conversation ID. Please try again.');
          return;
        }
 
        setIsUploading(true);
 
        // Upload and collect results
        const uploadResults: AttachedFile[] = [];
        for (const attachedFile of filesToUpload) {
          try {
            const uploadResponse = await fileUploadService.uploadFile(attachedFile.file, conversationId);
            uploadResults.push({ ...attachedFile, uploading: false, fileUrl: uploadResponse.fileUrl });
          } catch (error) {
            console.error('File upload failed:', error);
            uploadResults.push({ ...attachedFile, uploading: false, uploadError: 'Upload failed' });
          }
        }
 
        // Update state with results
        setAttachedFiles((prev) =>
          prev.map(f => {
            const uploaded = uploadResults.find(u => u.id === f.id);
            return uploaded || f;
          })
        );
 
        setIsUploading(false);
 
        // Check if any uploads failed
        const anyFailed = uploadResults.some(f => f.uploadError);
        if (anyFailed) {
          alert('Some files failed to upload. Please try again.');
          return;
        }
 
        // Update finalAttachedFiles with uploaded URLs
        finalAttachedFiles = attachedFiles.map(f => {
          const uploaded = uploadResults.find(u => u.id === f.id);
          return uploaded || f;
        });
      }
 
      const newAttachments: FileAttachment[] = finalAttachedFiles.map((a) => ({
        name: a.file.name,
        size: a.amsSize ?? a.file.size,
        type: a.file.type,
        preview: a.preview,
        fileUrl: a.fileUrl,
      }));
      // Merge with silently persisted file refs (deduplicate by fileUrl)
      // Silent refs are sent to the API for context but NOT shown in the chat UI
      const existingUrls = new Set(newAttachments.map(a => a.fileUrl).filter(Boolean));
      const silentRefs = persistedFileRefsRef.current
        .filter(r => !existingUrls.has(r.fileUrl))
        .map(r => ({ ...r, silent: true }));
      const merged = [...newAttachments, ...silentRefs];
      const fileAttachments: FileAttachment[] | undefined = merged.length > 0 ? merged : undefined;

      let apiPrompt: string | undefined;
      if (insertedEntities.size > 0) {
        let annotated = trimmed;
        insertedEntities.forEach((entity, name) => {
          annotated = annotated.replace(name, buildEntityXmlTag(entity));
        });
        apiPrompt = annotated;
      }

      onSend(trimmed, fileAttachments, apiPrompt);
      // Stop mic if it was running
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
        setIsListening(false);
      }
      micBaseValueRef.current = '';
      micAccumulatedRef.current = '';

      finalAttachedFiles.forEach((a) => { if (a.preview) URL.revokeObjectURL(a.preview); });
      setAttachedFiles([]);
      
      // Clear persisted files from sessionStorage after sending
      const storageKey = getAttachedFilesStorageKey();
      if (storageKey) {
        try {
          sessionStorage.removeItem(storageKey);
        } catch (err) {
          console.warn('Failed to clear attached files from sessionStorage:', err);
        }
      }
      
      setValue('');
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.overflowY = 'hidden';
      }
      setSuggestedQuestions([]);
      setSuggestionsActiveIdx(-1);
      clearInsertedNames();
    },
    [value, attachedFiles, disabled, onSend, isUploading, conversationId, clearInsertedNames, insertedEntities]
  );
 
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newVal = e.target.value;
    setValue(newVal);
    mentionOnValueChange(newVal);
      }, [mentionOnValueChange]);
 
    // Fetch AMP suggested questions with debounce
  //   if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
  //   if (!selectedAmpId || newVal.trim().length === 0) {
  //     setSuggestedQuestions([]);
  //     setSuggestionsActiveIdx(-1);
  //     return;
  //   }
  //   suggestionsDebounceRef.current = setTimeout(async () => {
  //     try {
  //       const questions = await getSuggestedQuestions(selectedAmpId, newVal.trim());
  //       setSuggestedQuestions(questions);
  //       setSuggestionsActiveIdx(-1);
  //     } catch {
  //       setSuggestedQuestions([]);
  //     }
  //   }, 300);
  // }, [mentionOnValueChange, selectedAmpId]);
   
 
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Keyboard navigation for suggested questions
      const hasSuggestions = suggestedQuestions.length > 0;
      if (hasSuggestions) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSuggestionsActiveIdx((prev) => Math.min(prev + 1, suggestedQuestions.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSuggestionsActiveIdx((prev) => Math.max(prev - 1, -1));
          return;
        }
        if (e.key === 'Enter' && suggestionsActiveIdx >= 0) {
          e.preventDefault();
          if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
          const selected = suggestedQuestions[suggestionsActiveIdx];
          setValue(selected);
          setSuggestedQuestions([]);
          setSuggestionsActiveIdx(-1);
          setTimeout(() => inputRef.current?.focus(), 0);
          return;
        }
        if (e.key === 'Escape') {
          setSuggestedQuestions([]);
          setSuggestionsActiveIdx(-1);
          return;
        }
      }
 
      const consumed = mentionOnKeyDown(e, value, (newVal) => {
        setValue(newVal);
        setTimeout(() => inputRef.current?.focus(), 0);
      });
      if (consumed) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!value.trim()) {
          handleMicClick();
        } else {
          handleSubmit(e);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSubmit, mentionOnKeyDown, value, suggestedQuestions, suggestionsActiveIdx]
  );
 
  const handleAttachClick = useCallback(() => {
    if (!isAuthenticated) {
      setSignInPrompt('Sign in to upload files');
      return;
    }
    setAttachMenuOpen((v) => !v);
  }, [isAuthenticated]);

  // Close the attach menu on outside click
  useEffect(() => {
    if (!attachMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
        setAttachMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [attachMenuOpen]);

  // AMS-drive selections are uploaded server-side (the AMS pre-signed download
  // URLs are not CORS-enabled, so the browser cannot fetch the bytes itself).
  // The resulting blob URLs are attached like regular uploads. Errors propagate
  // so the AMS dialog stays open and shows them.
  const handleAmsFilesUpload = useCallback(async (selections: AmsUploadSelection[]) => {
    if (!selections.length) return;
    setIsUploading(true);
    try {
      if (!conversationId) {
        throw new Error('Cannot attach AMS files without a conversation ID. Please try again.');
      }
      const refs = await fileUploadService.uploadAmsFiles(selections, conversationId);
      const newAttachments: AttachedFile[] = refs.map((r) => ({
        id: `ams-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: new File([], r.name),
        uploading: false,
        fileUrl: r.url,
        fromAms: true,
        amsSize: r.size,
      }));
      setAttachedFiles((prev) => [...prev, ...newAttachments]);
      // Persist file refs silently for subsequent messages, same as regular uploads
      refs.forEach((r) => {
        persistedFileRefsRef.current.push({ name: r.name, size: r.size ?? 0, type: '', fileUrl: r.url });
      });
    } finally {
      setIsUploading(false);
    }
  }, [conversationId]);

  const handleMicClick = useCallback(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in your browser. Try Chrome or Edge.');
      return;
    }
    if (!isAuthenticated) {
      setSignInPrompt('Sign in to use voice input');
      return;
    }

    // Toggle off if already listening
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    // Save the text already in the input before we start dictating
    micBaseValueRef.current = value;
    micAccumulatedRef.current = '';

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          micAccumulatedRef.current += (micAccumulatedRef.current ? ' ' : '') + t.trim();
        } else {
          interimTranscript += t;
        }
      }
      const base = micBaseValueRef.current;
      const sep = base.length > 0 && !base.endsWith(' ') ? ' ' : '';
      const spoken = micAccumulatedRef.current + (interimTranscript ? ' ' + interimTranscript : '');
      setValue(base + (spoken ? sep + spoken : ''));
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
      setTimeout(() => inputRef.current?.focus(), 0);
    };

    recognition.onerror = (event: any) => {
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        console.error('Speech recognition error:', event.error);
      }
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [isListening, isAuthenticated, value]);

  // Stop recognition on unmount
  useEffect(() => {
    return () => { recognitionRef.current?.stop(); };
  }, []);
 
  // Require text message to send - files alone cannot be sent
  const canSend = value.trim().length > 0;
 
  // Build mirror segments for colored mention highlighting
  const mirrorSegments = React.useMemo(() => {
    if (!insertedNames.length) return null;
    const escaped = insertedNames.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'g');
    const parts = value.split(regex);
    const nameSet = new Set(insertedNames);
    return parts.map((part, i) =>
      nameSet.has(part)
        ? <span key={i} className="chat-input__mention-token">{part}</span>
        : <span key={i}>{part}</span>
    );
  }, [value, insertedNames]);
 
  return (
    <div
      className={`chat-input-wrapper${isDragging ? ' chat-input-wrapper--dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag-over overlay */}
      {isDragging && (
        <div className="chat-input__drop-overlay">
          <FiPaperclip size={24} />
          <span>Drop files to attach</span>
        </div>
      )}

      {/* @mention Dropdown */}
      {mentionActive && (
        <InsuredMentionDropdown
          query={mentionQuery}
          results={mentionResults}
          loading={mentionLoading}
          activeIdx={mentionActiveIdx}
          onSelect={(ins) => {
            setValue(selectMention(ins, value));
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          onActiveChange={setMentionActiveIdx}
        />
      )}
 
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="chat-input__file-hidden"
        onChange={handleFileSelect}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,.json"
      />
 
      {/* Quick Actions */}
      <div className="chat-input__quick-actions">
        {QUICK_ACTIONS.map((action) => (
          <motion.button
            key={action.id}
            type="button"
            className="chat-input__quick-btn glass-tag"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => onSend(action.label)}
          >
            <span className="chat-input__quick-icon" style={{ color: action.color }}>
              {ACTION_ICONS[action.id]}
            </span>
            {action.label}
          </motion.button>
        ))}
      </div>
 
      {/* File Previews */}
      <SafeAnimatePresence>
        {attachedFiles.length > 0 && (
          <motion.div
            className="chat-input__attachments"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {attachedFiles.map((af) => (
              <motion.div
                key={af.id}
                className="chat-input__file-chip glass-surface"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.15 }}
              >
                {af.preview ? (
                  <img
                    src={af.preview}
                    alt={af.file.name}
                    className="chat-input__file-thumb"
                  />
                ) : (
                  <span className="chat-input__file-icon">
                    <FiFileText size={16} />
                  </span>
                )}
                <span className="chat-input__file-info">
                  <span className="chat-input__file-name">{af.file.name}</span>
                  <span className="chat-input__file-size">
                    {af.uploading ? 'Uploading...' : af.uploadError ? af.uploadError : af.fromAms ? (af.amsSize ? formatFileSize(af.amsSize) : 'From AMS') : formatFileSize(af.file.size)}
                  </span>
                </span>
                {af.uploading ? (
                  <span className="chat-input__file-uploading">
                    <FiClock size={12} />
                  </span>
                ) : (
                  <button
                    className="chat-input__file-remove"
                    type="button"
                    onClick={() => removeFile(af.id)}
                    title="Remove file"
                  >
                    <FiX size={12} />
                  </button>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </SafeAnimatePresence>
 
      {/* Input */}
      <form className="chat-input__form" onSubmit={handleSubmit}>
        {!hideAmpDropdown && <ChannelAmpDropdown selectedAmpId={selectedAmpId} onAmpSelect={onAmpSelect} overrideName={overrideAmpName} currentAgentId={overrideAgentId} />}
        <div className="chat-input__field-anchor">
        {!mentionActive && suggestedQuestions.length > 0 && (
          <div className="suggestions-dropdown">
            {suggestedQuestions.map((q, i) => (
              <button
                key={i}
                type="button"
                className={`suggestion-item${i === suggestionsActiveIdx ? ' active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
                  setValue(q);
                  setSuggestedQuestions([]);
                  setSuggestionsActiveIdx(-1);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
                onMouseEnter={() => setSuggestionsActiveIdx(i)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
                <span>{q}</span>
              </button>
            ))}
          </div>
        )}
        <div className="chat-input__field-wrap glass-surface">
          <span className="chat-input__slash">/</span>
          <div className="chat-input__field-inner">
            {mirrorSegments && (
              <div className="chat-input__mirror" aria-hidden="true">
                {mirrorSegments}
              </div>
            )}
            <textarea
              ref={inputRef}
              className={`chat-input__field${mirrorSegments ? ' chat-input__field--ghost' : ''}`}
              placeholder={placeholder || "Ask Insurance Agency AI anything…"}
              value={value}
              rows={1}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onBlur={() => {
                setTimeout(() => {
                  if (document.activeElement !== inputRef.current) {
                    setSuggestedQuestions([]);
                    setSuggestionsActiveIdx(-1);
                    if (suggestionsDebounceRef.current) clearTimeout(suggestionsDebounceRef.current);
                  }
                }, 200);
              }}
              disabled={disabled}
              autoComplete="off"
            />
          </div>
          <div className="chat-input__right-controls">
            {/* <span className="chat-input__model-tag">
              <RiSparklingFill size={12} style={{ marginRight: 3 }} />
              Sonnet 4.5
            </span> */}
            {!hideAttachButton && (
              <div ref={attachMenuRef} style={{ position: 'relative', display: 'flex' }}>
                <button
                  className="chat-input__icon-btn"
                  type="button"
                  title="Attach file"
                  onClick={handleAttachClick}
                >
                  <img src={fileIcon} alt="Attach" className="chat-input__attach-icon" />
                  {/* <FiPaperclip size={15} /> */}
                </button>
                {attachMenuOpen && (
                  <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, minWidth: 190, background: theme === 'dark' ? '#1e1e35' : '#ffffff', border: '1px solid var(--border-glass, rgba(255,255,255,0.09))', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', zIndex: 50, overflow: 'hidden' }}>
                    <div style={{ padding: '8px 16px 4px', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'Poppins, sans-serif' }}>Choose upload type:</div>
                    <button
                      type="button"
                      onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                      style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'Poppins, sans-serif', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                    >
                      <FiPaperclip size={13} /> Upload file
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAttachMenuOpen(false); setShowAmsSelector(true); }}
                      style={{ width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontFamily: 'Poppins, sans-serif', color: 'var(--text-primary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}
                    >
                      <FiFolder size={13} /> Select from AMS
                    </button>
                  </div>
                )}
              </div>
            )}
            <motion.button
              className={canSend
                ? "chat-input__send-btn"
                : `chat-input__icon-btn chat-input__mic-btn${isListening ? ' chat-input__mic-btn--active' : ''}`
              }
              type={canSend ? "submit" : "button"}
              title={canSend ? 'Send message' : (isListening ? 'Stop recording' : 'Voice input')}
              onClick={canSend ? undefined : handleMicClick}
              disabled={disabled}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
            >
              <motion.span
                key={canSend ? 'send-icon' : 'mic-icon'}
                initial={{ opacity: 0, scale: 0.4, rotate: canSend ? -30 : 30 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                transition={{ duration: 0.15, ease: 'backOut' }}
                className="chat-input__send-icon-wrap"
              >
                {canSend ? <FiSend size={16} /> : <FiMic size={15} />}
              </motion.span>
            </motion.button>
          </div>
        </div>
        </div>{/* end chat-input__field-anchor */}
      </form>
 
      <SafeAnimatePresence>
        {signInPrompt && (
          <SignInPrompt
            message={signInPrompt}
            onClose={() => setSignInPrompt(null)}
            variant="float"
          />
        )}
      </SafeAnimatePresence>

      {/* Select from AMS Drive — browse a client's AMS files and attach them to this chat */}
      <SelectFromAmsDialog
        visible={showAmsSelector}
        onClose={() => setShowAmsSelector(false)}
        onUpload={handleAmsFilesUpload}
      />
    </div>
  );
};
 
export default React.memo(ChatInput);
 
 